// src/acreageShadow.js — the M3 two-route acreage challenger, as ONE rule.
//
// M3 is a SHADOW CHALLENGER. It does not feed `pred_acres`, it does not touch a
// rendered number, and it is not promoted. Kevin, 2026-08-23: "Keep M3 as a
// shadow challenger. Do not promote it yet." What it does is get COMPUTED and
// RECORDED on every snapshot beside the shipped model, so that when CY2026 and
// CY2027 finals land there is a prospective track record to judge instead of
// another retrospective refit.
//
// ⚠ IT LIVES HERE, IN ONE FILE, ON PURPOSE. The snapshot cron writes the shadow
// row and `tools/check-acreage-shadow.js` scores it; two copies of this
// arithmetic is how the recorded challenger and the scored challenger come to be
// different models. This repo has that failure written down several times over
// (three news handlers, two guarantee engines, three normFarmerName copies), and
// a shadow model that silently disagrees with its own scorer would be the worst
// version of it — the whole point is a trustworthy record.
//
// ── THE TWO ROUTES ──────────────────────────────────────────────────────────
//
// Harvested grain acres = PLANTED x GRAIN SHARE, and a late spring attacks each
// separately:
//
//   ROUTE 1  the acres are never planted        2019: planted -8.7% vs its own
//                                               5-yr base, share NORMAL
//   ROUTE 2  they are planted, then chopped     2025: planted -5.5%, share
//                                               COLLAPSED to 46.4%
//
// Both are regressed on NASS week-22 percent-planted, each against its own
// 5-year base, and multiplied. Measured over 2015-2025: pace correlates 0.829
// with route 1 and 0.694 with route 2, and 0.830 with grain acres directly —
// higher than either component, because the product does not care which route a
// given season took.
//
// ── WHAT THIS IS NOT ────────────────────────────────────────────────────────
//
// Rolling-origin validation (refit at every origin, no future season informing
// any coefficient) puts M3 at 11.1% acre MAE against the shipped model's 17.8%
// at 1 September — a real gap, but on FOUR scored seasons, one of which is 2025
// itself. Leave-one-out said 8.4% and was flattered. Nine contemporaneous
// candidate variables were each tested individually against what pace leaves
// behind and NONE improved out of sample, so M3 is deliberately one variable per
// route and nothing else.
//
// Coefficient stability across the four origins: route 1 slope 0.00121-0.00148
// (CV 8%), route 2 0.00089-0.00133 (CV 16%), both positive throughout.

"use strict";

const mean = (a) => (a.length ? a.reduce((s, v) => s + v, 0) / a.length : null);

// Simple linear fit. Returns null rather than a degenerate line when x carries
// no spread — a flat predictor dressed as a regression is worse than an absence,
// because the caller cannot tell it apart from a real fit.
function fitLine(x, y) {
  if (!x || x.length < 3) return null;
  const mx = mean(x), my = mean(y);
  let n = 0, d = 0;
  for (let i = 0; i < x.length; i++) { n += (x[i] - mx) * (y[i] - my); d += (x[i] - mx) ** 2; }
  if (!(d > 0)) return null;
  const b = n / d;
  return { a: my - b * mx, b };
}

/**
 * paceByYear  { [year]: { [week]: cumulativePctPlanted } }  NASS crop progress
 * plantedByYear { [year]: acres }   FINAL state planted, prior seasons only
 * grainByYear   { [year]: acres }   FINAL state harvested-for-grain
 * target        the crop year being forecast
 * asOfWeek      NASS week the caller is standing in (pace is read at min(22, this))
 *
 * Every training season must be STRICTLY BEFORE `target`. The caller cannot opt
 * out of that — a shadow record built with hindsight is not a record.
 */
function acreageM3({ paceByYear, plantedByYear, grainByYear, target, asOfWeek }) {
  const wk = Math.min(22, asOfWeek || 22);
  const paceAt = (y) => {
    const wks = paceByYear[y];
    if (!wks) return null;
    let v = null;
    for (const k of Object.keys(wks)) {
      const w = Number(k);
      if (w > wk || wks[k] == null) continue;
      v = Math.max(v == null ? -1 : v, Number(wks[k]));
    }
    return v;
  };
  const train = Object.keys(grainByYear).map(Number)
    .filter((y) => y < target && plantedByYear[y] > 0 && grainByYear[y] > 0 && paceAt(y) != null)
    .sort((a, b) => a - b);

  const baseOf = (y, f) => {
    const p = train.filter((o) => o < y).slice(-5);
    return p.length >= 3 ? mean(p.map(f)) : null;
  };
  const PL = (y) => plantedByYear[y];
  const SH = (y) => grainByYear[y] / plantedByYear[y];

  const rows = train.filter((o) => baseOf(o, PL) != null && baseOf(o, SH) != null);
  if (rows.length < 4) {
    return { ok: false, reason: `only ${rows.length} usable training seasons before ${target} (need 4)` };
  }
  const x = rows.map(paceAt);
  const w1 = fitLine(x, rows.map((o) => PL(o) / baseOf(o, PL)));
  const w2 = fitLine(x, rows.map((o) => SH(o) / baseOf(o, SH)));
  if (!w1 || !w2) return { ok: false, reason: "pace carries no spread across the training seasons" };

  const basePl = baseOf(target, PL), baseSh = baseOf(target, SH);
  const pace = paceAt(target);
  if (basePl == null || baseSh == null) return { ok: false, reason: `no 5-year base available for ${target}` };
  if (pace == null) return { ok: false, reason: `no NASS planting pace for ${target} at or before week ${wk}` };

  const r1 = w1.a + w1.b * pace;          // planted, as a multiple of its base
  const r2 = w2.a + w2.b * pace;          // grain share, as a multiple of its base
  return {
    ok: true,
    pace_week: wk,
    pace_pct: pace,
    train_seasons: rows.length,
    train_from: rows[0], train_to: rows[rows.length - 1],
    route1_planted_mult: r1,
    route2_share_mult: r2,
    planted_base: basePl,
    share_base: baseSh,
    planted_est: basePl * r1,
    share_est: baseSh * r2,
    grain_acres_est: basePl * r1 * baseSh * r2,
    coef: { route1: w1, route2: w2 },
  };
}


// ── HOW WRONG THE ACREAGE HAS BEEN — the band under the production headline ──
//
// Market Intelligence prints a production number to the bushel (79,729,972 bu on
// 2026-08-23) and it is YIELD x ACRES. The yield half has a stated uncertainty.
// The acreage half had none on screen, and today ALL 39 counties are on the
// `projected` branch — `acres_source: "projected"` on every one — which means the
// headline rests on a 5-year mean of prior planted acres, with no NASS county
// harvested figure published yet to correct it.
//
// So this measures that rule against itself, rolling origin: for each season,
// project from the FIVE PRIOR SEASONS ONLY and compare with what was harvested.
//
// ⚠ IT IS MEASURED AT THE STATE LEVEL ON PURPOSE, AND THE COUNTY VERSION IS A
// TRAP. Summed over the 39 modelled counties the same rule reports MAE 15.2% and
// a +28.9% miss in 2025 — but 2025's "actual" is summed over 32 reporting
// counties against a mean built from years with 34-39, so most of that gap is
// NASS SUPPRESSION, not a forecast error. Matching the county set both ways
// (a county counts only if it reports in the target year and all five priors)
// leaves **two** usable origins, which is not a distribution. The state series
// has no coverage problem at all, so that is what the band is built from.
//
// Measured 2026-08-23, NY corn grain acres, origins 2020-2025:
//
//   2020 +0.7%   2021 -5.0%   2022 -3.2%   2023 -6.2%   2024 +1.3%   2025 +26.4%
//   n=6  MAE 7.1%  bias +2.3%  worst +26.4%
//
// **THE SKEW IS THE FINDING, SO THE BAND IS NOT SYMMETRIC.** Five of six seasons
// land inside +/-6.2% and the sixth is +26.4% — the season the grain share
// collapsed and the acres were chopped rather than combined. An SD would average
// that away into a tidy +/-11% that is wrong in both directions: too wide for a
// normal year and far too narrow for a 2025. The empirical spread is reported as
// it fell.
//
// **IT IS THE ACREAGE HALF ONLY.** It says nothing about whether the yield is
// right, and a caller that prints it as a production confidence interval is
// overstating what was measured. The screen must say which half it covers.
function acreageBand({ grainByYear, targetYear, window = 5, minOrigins = 4 }) {
  const years = Object.keys(grainByYear || {}).map(Number)
    .filter((y) => y < targetYear && grainByYear[y] > 0).sort((a, b) => a - b);
  const errors = [];
  for (const y of years) {
    const prior = years.filter((p) => p < y).slice(-window);
    if (prior.length < window) continue;
    const pred = prior.reduce((s, p) => s + grainByYear[p], 0) / prior.length;
    errors.push({ year: y, pred, actual: grainByYear[y], err_pct: (pred / grainByYear[y] - 1) * 100 });
  }
  if (errors.length < minOrigins) {
    return { ok: false, reason: `only ${errors.length} rolling origins available (need ${minOrigins})` };
  }
  const e = errors.map((x) => x.err_pct);
  // Belt and braces on an empty set. The floor above already covers it, but a
  // reduce over [] with an `errors[0]` seed returns undefined and the next line
  // throws a TypeError — which surfaces as a crashed page rather than as the
  // refusal this function is supposed to give. A guard that only holds while
  // the guard above it holds is not a guard.
  if (!e.length) return { ok: false, reason: "no rolling origins could be built" };
  const hi = Math.max(...e), lo = Math.min(...e);
  const worst = errors.reduce((w, x) => (Math.abs(x.err_pct) > Math.abs(w.err_pct) ? x : w), errors[0]);
  return {
    ok: true,
    n: errors.length,
    from_year: errors[0].year,
    to_year: errors[errors.length - 1].year,
    mae_pct: e.reduce((s, v) => s + Math.abs(v), 0) / e.length,
    bias_pct: e.reduce((s, v) => s + v, 0) / e.length,
    worst_pct: worst.err_pct,
    worst_year: worst.year,
    // Production multipliers. An acreage projection that ran `err` high means the
    // realized crop was pred/(1+err) — so the WORST OVER-projection sets the LOW
    // end of production, not the high one. Getting that inversion backwards would
    // print an optimistic floor, which is the one direction that costs money.
    low_mult: 1 / (1 + hi / 100),
    high_mult: 1 / (1 + lo / 100),
    errors,
  };
}

module.exports = { acreageM3, fitLine, acreageBand };
